/**
 * Сравнение двух версий текста по фрагментам: что убрано, что добавлено, что осталось.
 *
 * Единица сравнения — слово вместе с прилегающим пробельным промежутком: посимвольное
 * сравнение рвёт слова на неразличимые куски, а построчное не показывает правку внутри строки.
 */

/**
 * Слово вместе с прилегающими пробельными промежутками; вторая ветка — текст из одних пробелов.
 *
 * Промежуток обязан ехать вместе со словом: отдельным токеном он совпадает в любых двух версиях
 * и склеивает сравнение по случайным местам — правка выходит чересполосицей слов обеих версий.
 */
const TEXT_TOKEN_PATTERN = /\s*\S+\s*|\s+/g;

/**
 * Порог, за которым точное сравнение не считается: таблица сравнения растёт как
 * произведение длин, а комментарий такого объёма всё равно читается как заменённый целиком.
 */
const MAX_COMPARED_CELLS = 250_000;

/** Фрагмент сравнения версий: общий, убранный из прежней версии или добавленный в новой. */
export type TextDiffFragment = {
  kind: 'equal' | 'removed' | 'added';
  text: string;
};

/** Разбивает текст на единицы сравнения. */
export const tokenizeText = (text: string): string[] => text.match(TEXT_TOKEN_PATTERN) ?? [];

/**
 * Сравниваются сами слова: прилегающие пробельные промежутки на совпадение не влияют, поэтому
 * ни перенос строки вместо пробела, ни отсутствие пробела в конце текста правкой не считаются.
 */
const normalizeToken = (token: string): string => token.trim() || ' ';

/** Длины общих начал и общих концов совпадают — их сравнивать незачем. */
const countCommonPrefix = (previous: string[], next: string[]): number => {
  let count = 0;
  while (
    count < previous.length &&
    count < next.length &&
    normalizeToken(previous[count]) === normalizeToken(next[count])
  )
    count++;
  return count;
};

const countCommonSuffix = (previous: string[], next: string[], limit: number): number => {
  let count = 0;
  while (
    count < limit &&
    normalizeToken(previous[previous.length - 1 - count]) === normalizeToken(next[next.length - 1 - count])
  )
    count++;
  return count;
};

/** Таблица длин наибольшей общей подпоследовательности токенов. */
const buildLcsTable = (previous: string[], next: string[]): number[][] => {
  const table: number[][] = Array.from({ length: previous.length + 1 }, () =>
    new Array<number>(next.length + 1).fill(0),
  );
  for (let i = previous.length - 1; i >= 0; i--) {
    for (let j = next.length - 1; j >= 0; j--) {
      table[i][j] =
        normalizeToken(previous[i]) === normalizeToken(next[j])
          ? table[i + 1][j + 1] + 1
          : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  return table;
};

/** Дописывает фрагмент в хвост, склеивая его с однородным соседом. */
const appendFragment = (fragments: TextDiffFragment[], kind: TextDiffFragment['kind'], text: string): void => {
  const last = fragments.at(-1);
  if (last?.kind === kind) last.text += text;
  else fragments.push({ kind, text });
};

const countCommonCharPrefix = (previous: string, next: string, limit: number): number => {
  let count = 0;
  while (count < limit && previous[count] === next[count]) count++;
  return count;
};

const countCommonCharSuffix = (previous: string, next: string, limit: number): number => {
  let count = 0;
  while (count < limit && previous[previous.length - 1 - count] === next[next.length - 1 - count]) count++;
  return count;
};

/**
 * Разбирает пару «убрано — добавлено», в которой правка лишь дописала текст к прежнему или
 * убрала часть его: совпавшая часть возвращается в общий текст, помеченной остаётся только разница.
 *
 * `null` — версии разошлись по существу (одно слово заменено другим): там совпадение букв
 * случайно, и разбор по буквам только зашумил бы сравнение.
 */
const refineAppendedText = (removed: string, added: string): TextDiffFragment[] | null => {
  const shortest = Math.min(removed.length, added.length);
  const prefix = countCommonCharPrefix(removed, added, shortest);
  const suffix = countCommonCharSuffix(removed, added, shortest - prefix);
  // Дописка — это когда короткая версия совпала целиком; всё остальное считается заменой
  if (prefix + suffix !== shortest) return null;

  const isAppended = removed.length < added.length;
  const longest = isAppended ? added : removed;
  const difference = longest.slice(prefix, longest.length - suffix);
  if (difference === '') return null;

  const fragments: TextDiffFragment[] = [];
  if (prefix > 0) fragments.push({ kind: 'equal', text: added.slice(0, prefix) });
  fragments.push({ kind: isAppended ? 'added' : 'removed', text: difference });
  if (suffix > 0) fragments.push({ kind: 'equal', text: added.slice(added.length - suffix) });
  return fragments;
};

/** Проходит по соседним парам «убрано — добавлено» и уточняет те из них, что оказались дописками. */
const refineFragments = (fragments: TextDiffFragment[]): TextDiffFragment[] => {
  const refined: TextDiffFragment[] = [];

  for (let index = 0; index < fragments.length; index++) {
    const current = fragments[index];
    const next = fragments[index + 1];
    const isEditPair =
      next !== undefined &&
      ((current.kind === 'removed' && next.kind === 'added') || (current.kind === 'added' && next.kind === 'removed'));

    const pieces = isEditPair
      ? refineAppendedText(
          current.kind === 'removed' ? current.text : next.text,
          current.kind === 'added' ? current.text : next.text,
        )
      : null;

    if (pieces === null) {
      appendFragment(refined, current.kind, current.text);
      continue;
    }

    for (const piece of pieces) appendFragment(refined, piece.kind, piece.text);
    index++;
  }

  return refined;
};

/**
 * Сравнивает прежнюю и новую версии текста и возвращает их фрагменты в порядке чтения новой
 * версии: убранный фрагмент стоит там, где он был, добавленный — там, где появился.
 */
export const diffText = (previous: string, next: string): TextDiffFragment[] => {
  const previousTokens = tokenizeText(previous);
  const nextTokens = tokenizeText(next);

  const prefix = countCommonPrefix(previousTokens, nextTokens);
  const suffix = countCommonSuffix(
    previousTokens,
    nextTokens,
    Math.min(previousTokens.length, nextTokens.length) - prefix,
  );
  const previousMiddle = previousTokens.slice(prefix, previousTokens.length - suffix);
  const nextMiddle = nextTokens.slice(prefix, nextTokens.length - suffix);

  // Общий текст берётся из новой версии: правкой пробельного промежутка считать нечего,
  // а показывается и отмеряется по позициям именно она
  const fragments: TextDiffFragment[] = [];
  if (prefix > 0) appendFragment(fragments, 'equal', nextTokens.slice(0, prefix).join(''));

  if (previousMiddle.length * nextMiddle.length > MAX_COMPARED_CELLS) {
    if (previousMiddle.length > 0) appendFragment(fragments, 'removed', previousMiddle.join(''));
    if (nextMiddle.length > 0) appendFragment(fragments, 'added', nextMiddle.join(''));
  } else {
    const table = buildLcsTable(previousMiddle, nextMiddle);
    let i = 0;
    let j = 0;
    while (i < previousMiddle.length && j < nextMiddle.length) {
      if (normalizeToken(previousMiddle[i]) === normalizeToken(nextMiddle[j])) {
        appendFragment(fragments, 'equal', nextMiddle[j]);
        i++;
        j++;
      } else if (table[i + 1][j] >= table[i][j + 1]) {
        appendFragment(fragments, 'removed', previousMiddle[i]);
        i++;
      } else {
        appendFragment(fragments, 'added', nextMiddle[j]);
        j++;
      }
    }
    if (i < previousMiddle.length) appendFragment(fragments, 'removed', previousMiddle.slice(i).join(''));
    if (j < nextMiddle.length) appendFragment(fragments, 'added', nextMiddle.slice(j).join(''));
  }

  if (suffix > 0) appendFragment(fragments, 'equal', nextTokens.slice(nextTokens.length - suffix).join(''));

  return refineFragments(fragments).filter((fragment) => fragment.text !== '');
};
